import React, { useState } from 'react'
import { ChevronLeft, CheckCircle2, Shield, FileText, ArrowRight, Info, Flag, RefreshCw } from 'lucide-react'
import { buildTreeForTypology } from '../typologyData/trees'
import { useTypology } from '../../../hooks/useTypology'

const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z_]+)\s*\}\}/g

/**
 * Replace {{name}} / {{occupation}} / {{risk}} / {{txSummary}} placeholders in
 * each node's `context` against runtime patron + transaction data. Other
 * fields (question, why, options) are static and pass through unchanged.
 */
function interpolateTreeContext(dbTree, { patron, transactions, fmt$ }) {
  if (!dbTree || typeof dbTree !== 'object') return null
  const name = patron?.name || 'Patron'
  const occupation = patron?.occupation || '—'
  const risk = patron?.risk ?? 0
  const fmt = fmt$ || ((n) => '$' + (n ?? 0).toLocaleString())
  const txTotal = (transactions || []).reduce((a, t) => a + (t.amount || 0), 0)
  const txSummary = (transactions || []).length
    ? `${transactions.length} txns, ${fmt(txTotal)}`
    : 'Review linked transactions'
  const ctx = { name, occupation, risk, txSummary }
  const out = {}
  for (const [nodeId, node] of Object.entries(dbTree)) {
    if (!node) { out[nodeId] = node; continue }
    if (typeof node.context === 'string') {
      out[nodeId] = { ...node, context: node.context.replace(PLACEHOLDER_RE, (_, k) => ctx[k] ?? '') }
    } else {
      out[nodeId] = node
    }
  }
  return out
}

const C = {
  pri: '#0a5169', priM: '#e8f2f6',
  red: '#dc2626', redBg: '#fef2f2', redBd: '#fecaca',
  amber: '#d97706', amberBg: '#fffbeb', amberBd: '#fde68a',
  green: '#16a34a', greenBg: '#f0fdf4', greenBd: '#bbf7d0',
  purple: '#7c3aed', purpleBg: '#f5f3ff', purpleBd: '#ddd6fe',
  slate: '#64748b', slateL: '#94a3b8',
  ink: '#0f172a',
}

export default function InvestigationTreeStrategy({ caseData, patron, transactions = [], overview, fmt$, typology = 'structuring' }) {
  // DB-first: a tenant admin can tune the decision tree in the typologies
  // table. While loading, or when the DB row has no tree, fall back to the
  // bundled JS so the strategy renders instantly with sensible defaults.
  const dbTypology = useTypology(typology)
  const TREE = React.useMemo(
    () => interpolateTreeContext(dbTypology?.tree, { patron, transactions, fmt$ })
      || buildTreeForTypology(typology, patron, transactions, fmt$),
    [dbTypology, typology, patron, transactions, fmt$]
  )
  const [path, setPath] = useState(['root'])
  const [answers, setAnswers] = useState({})
  const [selected, setSelected] = useState(null)
  const [narrative, setNarrative] = useState('')
  const [done, setDone] = useState(false)

  const currentId = path[path.length - 1]
  const currentNode = TREE[currentId]
  const isTerminal = currentNode?.terminal

  const choose = (oi) => {
    const opt = currentNode.options[oi]
    setAnswers(prev => ({ ...prev, [currentId]: opt.label }))
    setSelected(null)
    setPath(prev => [...prev, opt.next])
  }

  const goBack = (i) => {
    if (i >= path.length - 1) return
    const newPath = path.slice(0, i + 1)
    const toRemove = path.slice(i)
    setAnswers(prev => { const n = { ...prev }; toRemove.forEach(id => delete n[id]); return n })
    setPath(newPath)
    setSelected(null)
  }

  const reset = () => { setPath(['root']); setAnswers({}); setSelected(null); setDone(false) }

  if (done && isTerminal) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 40px' }}>
        <div style={{ width: 60, height: 60, borderRadius: '50%', background: C.greenBg, border: `2px solid ${C.greenBd}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <CheckCircle2 size={28} color={C.green} />
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.green, marginBottom: 8 }}>Investigation Finalised</div>
        <div style={{ fontSize: 13, color: C.slate }}>Decision and narrative recorded.</div>
      </div>
    )
  }

  if (isTerminal && currentNode) {
    const Icon = currentNode.Icon
    return (
      <div className="space-y-4">
        <div style={{ background: `${currentNode.colour}15`, border: `1px solid ${currentNode.colour}44`, borderLeft: `5px solid ${currentNode.colour}`, borderRadius: 10, padding: '20px 22px' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#fff', border: `2px solid ${currentNode.colour}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={21} color={currentNode.colour} />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: currentNode.colour, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>Tree Recommendation</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: currentNode.colour, marginBottom: 4 }}>{currentNode.title}</div>
              <div style={{ fontSize: 12, color: currentNode.colour, fontStyle: 'italic' }}>{currentNode.summary}</div>
            </div>
          </div>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>CO Narrative</div>
          <div style={{ padding: '14px 16px' }}>
            <textarea value={narrative} onChange={e => setNarrative(e.target.value)} placeholder="Summarise your conclusion. What were the key factors?" style={{ width: '100%', minHeight: 90, fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 7, padding: '10px 12px', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', color: '#334155', lineHeight: 1.6 }} />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={reset} style={{ padding: '10px 20px', background: C.slateBg, color: C.slate, border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={14} /> Restart
              </button>
              <button onClick={() => narrative.length >= 30 && setDone(true)} disabled={narrative.length < 30} style={{ padding: '10px 24px', background: narrative.length >= 30 ? currentNode.colour : '#e2e8f0', color: narrative.length >= 30 ? '#fff' : C.slateL, border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 800, cursor: narrative.length >= 30 ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 7 }}>
                <CheckCircle2 size={14} /> Finalise Investigation
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!currentNode || !currentNode.options) return null

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20 }}>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.slateL, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Decision Path</div>
        {path.filter(id => !TREE[id]?.terminal).map((nodeId, i) => (
          <div key={nodeId + i} onClick={() => goBack(i)} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '8px 12px', borderRadius: 8, background: i === path.length - 1 ? C.priM : '#fff', border: `1px solid ${i === path.length - 1 ? C.pri + '55' : '#f1f5f9'}`, cursor: i < path.length - 1 ? 'pointer' : 'default', marginBottom: 6 }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: i === path.length - 1 ? C.pri : (answers[nodeId] ? C.greenBg : C.slateBg), border: `1px solid ${i === path.length - 1 ? C.pri : (answers[nodeId] ? C.greenBd : '#e2e8f0')}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
              {answers[nodeId] ? <CheckCircle2 size={11} color={C.green} /> : <span style={{ fontSize: 9, fontWeight: 800, color: i === path.length - 1 ? '#fff' : C.slateL }}>{i + 1}</span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: i === path.length - 1 ? C.pri : C.slateL, textTransform: 'uppercase', marginBottom: 1 }}>{TREE[nodeId]?.phase}</div>
              <div style={{ fontSize: 11, color: i === path.length - 1 ? C.pri : C.ink, fontWeight: i === path.length - 1 ? 700 : 400, lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{TREE[nodeId]?.question}</div>
              {answers[nodeId] && <div style={{ fontSize: 10, color: C.green, marginTop: 3 }}>→ {answers[nodeId]}</div>}
            </div>
          </div>
        ))}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '16px 22px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: C.pri, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>{currentNode.phase}</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.ink, lineHeight: 1.4, marginBottom: 14 }}>{currentNode.question}</div>
          {currentNode.why && (
            <div style={{ background: C.priM, border: `1px solid ${C.pri}22`, borderRadius: 8, padding: '10px 14px', display: 'flex', gap: 8, marginBottom: currentNode.context ? 10 : 0 }}>
              <Info size={14} color={C.pri} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 12, color: C.pri, lineHeight: 1.55 }}>{currentNode.why}</div>
            </div>
          )}
          {currentNode.context && (
            <div style={{ background: C.amberBg, border: `1px solid ${C.amberBd}`, borderRadius: 8, padding: '10px 14px', display: 'flex', gap: 8 }}>
              <Flag size={13} color={C.amber} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 11, color: '#78350f', lineHeight: 1.5 }}><strong>Case data: </strong>{currentNode.context}</div>
            </div>
          )}
        </div>
        <div style={{ padding: '18px 22px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.slateL, marginBottom: 12, textTransform: 'uppercase' }}>What best describes what you've found?</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
            {currentNode.options.map((opt, oi) => {
              const isSel = selected === oi
              return (
                <div key={oi} onClick={() => setSelected(oi)} style={{ border: `2px solid ${isSel ? C.pri : '#e8eef2'}`, borderRadius: 10, padding: '13px 16px', cursor: 'pointer', background: isSel ? C.priM : '#fff', transition: 'all 0.12s' }}>
                  <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${isSel ? C.pri : '#d1d5db'}`, background: isSel ? C.pri : '#fff', flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isSel && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: isSel ? 700 : 400, color: isSel ? C.pri : C.ink, lineHeight: 1.4 }}>{opt.label}</div>
                      {opt.hint && isSel && <div style={{ fontSize: 11, color: C.slate, marginTop: 6, lineHeight: 1.45, fontStyle: 'italic', borderTop: `1px solid ${C.pri}22`, paddingTop: 6 }}>Note: {opt.hint}</div>}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          <button onClick={() => selected !== null && choose(selected)} disabled={selected === null} style={{ width: '100%', padding: '12px 0', background: selected !== null ? C.pri : '#e2e8f0', color: selected !== null ? '#fff' : C.slateL, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: selected !== null ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            Next <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}
